import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Flex, Text } from 'rebass/styled-components';
import toast from 'react-hot-toast';
import { Button, Loader, MessageBlock } from '@getro/rombo';
import { useUpdateGeneralSettingsMutation } from 'services/network';
import { trackEvent } from 'helpers/analytics';
import MfaStatusBadge from 'components/atoms/mfaStatusBadge';
import { loadedSaveGeneralSettings } from '../../actions';
import strings from '../../../../strings';
import MfaRequiredConfirmModal from './MfaRequiredConfirmModal';

const Security = ({ mfaRequired, isLoading, fetchError }) => {
  const dispatch = useDispatch();
  const [updateSettings, { isLoading: isSaving }] = useUpdateGeneralSettingsMutation();
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingEnforcedValue, setPendingEnforcedValue] = useState(null);
  const pendingActionRef = useRef(null);
  const confirmingRef = useRef(false);

  const openConfirm = useCallback((action) => {
    pendingActionRef.current = action;
    setPendingAction(action);
  }, []);

  const handleCancel = useCallback(() => {
    if (confirmingRef.current || isSaving) {
      return;
    }

    pendingActionRef.current = null;
    setPendingAction(null);
  }, [isSaving]);

  const handleConfirm = useCallback(async () => {
    const action = pendingActionRef.current;
    if (!action || confirmingRef.current) {
      return;
    }

    const newValue = action === 'enable';
    confirmingRef.current = true;

    try {
      await updateSettings({ mfaRequired: newValue }).unwrap();
      dispatch(loadedSaveGeneralSettings({ values: { mfaRequired: newValue } }));
      setPendingEnforcedValue(newValue);
      pendingActionRef.current = null;
      setPendingAction(null);
      toast('Changes saved successfully', { icon: 'success', id: 'mfa-required-settings' });
    } catch (error) {
      toast(strings.genericError, { icon: 'error', id: 'mfa-required-settings' });
    } finally {
      confirmingRef.current = false;
    }
  }, [dispatch, updateSettings]);

  useEffect(() => {
    if (pendingEnforcedValue === null || mfaRequired !== pendingEnforcedValue) {
      return;
    }

    trackEvent('network_settings:edit_mfa_settings', { enforced: pendingEnforcedValue });
    setPendingEnforcedValue(null);
  }, [mfaRequired, pendingEnforcedValue]);

  return (
    <Flex flexDirection="column" sx={{ gap: '16px' }}>
      <Text
        pt="40px"
        pb="8px"
        fontSize="12px"
        color="text.subtle"
        sx={{ borderBottom: 1, borderBottomColor: 'neutral.100', textTransform: 'uppercase', letterSpacing: '1px' }}
      >
        Security
      </Text>
      {fetchError && <MessageBlock type="error">{strings.genericError}</MessageBlock>}
      {!fetchError && isLoading && <Loader size="small" />}
      {!fetchError && !isLoading && (
        <Flex flexDirection="column" sx={{ alignItems: 'flex-start', gap: '8px' }}>
          <Flex sx={{ flexDirection: 'column', rowGap: '4px' }}>
            <Flex alignItems="center" sx={{ gap: '8px' }}>
              <Text lineHeight="140%" fontSize="14px" fontWeight="500" as="h4">
                Enforce two-factor authentication
              </Text>
              <MfaStatusBadge enabled={mfaRequired} />
            </Flex>
            <Text as="p" color="text.main" lineHeight="20px" fontSize="14px">
              When enabled, all team members must set up two-factor authentication to log in.
            </Text>
          </Flex>
          <Button
            size="small"
            variant="secondary"
            onClick={() => openConfirm(mfaRequired ? 'disable' : 'enable')}
            data-cy={mfaRequired ? 'mfa-required-disable' : 'mfa-required-enable'}
          >
            {mfaRequired ? 'Disable' : 'Enable'}
          </Button>
        </Flex>
      )}
      {pendingAction && (
        <MfaRequiredConfirmModal
          action={pendingAction}
          isSubmitting={isSaving}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
        />
      )}
    </Flex>
  );
};

Security.propTypes = {
  mfaRequired: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  fetchError: PropTypes.bool.isRequired,
};

export default Security;
